import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDraggable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { format, differenceInMinutes } from "date-fns";
import {
  Mail,
  Phone,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  StickyNote,
  User,
  Check,
  Globe,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CalendarEvent, CalendarEventType } from "@/types/calendar-view";
import { getEventTypeClasses } from "@/types/calendar-view";

// ---------------------------------------------------------------------------
// Icons per event type
// ---------------------------------------------------------------------------

const TYPE_ICONS: Record<CalendarEventType, React.ElementType> = {
  meeting: CalendarIcon,
  call: Phone,
  email: Mail,
  task: Clock,
  proposal: FileText,
  note: StickyNote,
  external: Globe,
};

// ---------------------------------------------------------------------------
// CalendarEventBlock
// Draggable + resizable event block for the week/day grid.
// ---------------------------------------------------------------------------

interface CalendarEventBlockProps {
  event: CalendarEvent;
  top: number;
  height: number;
  width?: string;
  left?: string;
  compact?: boolean;
  slotHeight?: number;
  onEventClick?: (event: CalendarEvent) => void;
  onResize?: (eventId: string, newDurationMin: number) => void;
}

const MIN_HEIGHT_PX = 24;
const MIN_DURATION_MIN = 15;

export const CalendarEventBlock = React.memo(function CalendarEventBlock({
  event,
  top,
  height,
  width = "100%",
  left = "0",
  compact = false,
  slotHeight = 64,
  onEventClick,
  onResize,
}: CalendarEventBlockProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [resizeStartHeight, setResizeStartHeight] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(height);
  const blockRef = useRef<HTMLDivElement | null>(null);

  // Update height from props when not resizing
  useEffect(() => {
    if (!isResizing) setCurrentHeight(height);
  }, [height, isResizing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: event.id,
    data: { event },
    disabled: isResizing,
  });

  const style: React.CSSProperties = {
    position: "absolute",
    top: `${top}px`,
    height: `${Math.max(currentHeight, MIN_HEIGHT_PX)}px`,
    width,
    left,
    zIndex: isDragging ? 50 : isResizing ? 45 : 10,
    ...(transform
      ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
      : {}),
  };

  const isDone = event.status === "done";
  const isCanceled = event.status === "canceled";
  const Icon = TYPE_ICONS[event.type] ?? User;
  const duration = differenceInMinutes(event.end, event.start);

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeStartY(e.clientY);
      setResizeStartHeight(currentHeight);
    },
    [currentHeight]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const delta = e.clientY - resizeStartY;
      const newHeight = Math.max(MIN_HEIGHT_PX, resizeStartHeight + delta);
      setCurrentHeight(newHeight);
    },
    [isResizing, resizeStartY, resizeStartHeight]
  );

  const handleResizeEnd = useCallback(() => {
    if (!isResizing) return;
    setIsResizing(false);

    // Calculate new duration from height
    const pxPerMinute = slotHeight / 30;
    const newDuration = Math.max(
      MIN_DURATION_MIN,
      Math.round(currentHeight / pxPerMinute / 15) * 15
    );
    if (onResize && newDuration !== duration) {
      onResize(event.id, newDuration);
    }
  }, [isResizing, currentHeight, slotHeight, onResize, event.id, duration]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const isSmall = currentHeight < 36;

  const blockContent = (
    <div
      ref={(node) => {
        setNodeRef(node);
        blockRef.current = node;
      }}
      style={style}
      className={cn(
        "rounded-md border-2 overflow-hidden cursor-pointer select-none transition-shadow",
        "hover:shadow-md",
        getEventTypeClasses(event.type, isDone),
        isDragging && "opacity-60 shadow-lg scale-105",
        isResizing && "shadow-lg",
        (isDone || isCanceled) && "opacity-60"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onEventClick?.(event);
      }}
    >
      {/* Main drag area */}
      <div
        {...listeners}
        {...attributes}
        className={cn(
          "h-full cursor-grab active:cursor-grabbing",
          isSmall ? "flex items-center gap-1 px-1.5" : "flex flex-col p-1.5"
        )}
      >
        {isSmall ? (
          <>
            <Icon className="h-3 w-3 flex-shrink-0" />
            <span
              className={cn(
                "text-[10px] font-medium truncate leading-tight",
                (isDone || isCanceled) && "line-through"
              )}
            >
              {event.title}
            </span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 min-w-0">
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span
                className={cn(
                  "text-xs font-semibold truncate leading-tight",
                  (isDone || isCanceled) && "line-through"
                )}
              >
                {event.title}
              </span>
            </div>
            {!compact && currentHeight >= 52 && (
              <span className="text-[10px] opacity-80 truncate mt-0.5">
                {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
              </span>
            )}
            {!compact && currentHeight >= 68 && event.meta?.companyName && (
              <span className="text-[10px] opacity-70 truncate mt-0.5">
                {event.meta.companyName}
                {event.meta.leadName ? ` · ${event.meta.leadName}` : ""}
              </span>
            )}
          </>
        )}
      </div>

      {/* Done badge */}
      {isDone && (
        <div className="absolute -top-1 -right-1 bg-success rounded-full p-0.5">
          <Check className="h-2.5 w-2.5 text-white" />
        </div>
      )}

      {/* Resize handle */}
      {onResize && (
        <div
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-primary/20 transition-colors flex items-center justify-center"
          onMouseDown={handleResizeStart}
        >
          <div className="w-6 h-0.5 bg-border rounded-full opacity-40 hover:opacity-100 transition-opacity" />
        </div>
      )}
    </div>
  );

  // Wrap in tooltip for full details on hover
  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>{blockContent}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-[240px]">
        <p className="font-semibold text-sm">{event.title}</p>
        <p className="text-xs text-muted-foreground">
          {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")} (
          {duration}min)
        </p>
        {event.meta?.leadName && (
          <p className="text-xs mt-1">{event.meta.leadName}</p>
        )}
        {event.meta?.companyName && (
          <p className="text-xs text-muted-foreground">
            {event.meta.companyName}
          </p>
        )}
        {event.meta?.dealTitle && (
          <p className="text-xs text-muted-foreground">
            {event.meta.dealTitle}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
});
